import { Container, Graphics } from "pixi.js";
import { ARENA_H, ARENA_W, COLOR, DRAGON, PLAYER, SWORD } from "../config.ts";
import { HZ_CIRCLE } from "../entities/hazard.ts";
import {
  EN_ARCHER,
  EN_BEAST,
  EN_BOMBER,
  EN_HEN,
  EN_LASER,
  EN_METEOR,
  EN_NINJA,
  MODE_ACT,
  MODE_ENTER,
  MODE_LEAVE,
  type Enemy,
} from "../entities/enemy.ts";
import { TAU, clamp, lerp } from "../core/vec.ts";
import type { World } from "../world.ts";

/**
 * Draws the World each frame in the flat, high-contrast vector style. Uses
 * render interpolation (`alpha`) between fixed sim steps for smooth 60fps+
 * motion. Everything is redrawn into pooled Graphics each frame — cheap at
 * these densities; can move to instanced sprites if profiling demands.
 *
 * Cheap "glow" is faked by drawing a larger translucent shape behind each
 * entity (no fullscreen bloom filter — keeps low-end phones at 60fps). Motion
 * is sold with streak trails: bullets smear opposite their velocity and the
 * player leaves a fading afterimage ribbon.
 */

const TRAIL_LEN = 16; // player afterimage samples

interface Star {
  x: number;
  y: number;
  size: number;
  speed: number; // parallax fall speed
  tw: number; // twinkle phase
}


export class Renderer {
  private bg = new Graphics();
  private stars = new Graphics();
  private glow = new Graphics();
  private trails = new Graphics();
  private particles = new Graphics();
  private hearts = new Graphics();
  private hazards = new Graphics();
  private enemies = new Graphics();
  private dragon = new Graphics();
  private player = new Graphics();
  private flash = new Graphics();
  private time = 0;

  private readonly starField: Star[] = [];
  private readonly trailX = new Float32Array(TRAIL_LEN);
  private readonly trailY = new Float32Array(TRAIL_LEN);
  private trailHead = 0;
  private trailCount = 0;

  constructor(arena: Container) {
    this.drawBackground();
    this.seedStars();
    arena.addChild(
      this.bg,
      this.stars,
      this.glow,
      this.trails,
      this.particles,
      this.hazards,
      this.enemies,
      this.dragon,
      this.hearts,
      this.player,
      this.flash,
    );
  }

  /** Deep-space backdrop: pure dark field (the animated starfield sits on top). */
  private drawBackground(): void {
    const g = this.bg;
    g.clear();
    g.rect(0, 0, ARENA_W, ARENA_H).fill({ color: COLOR.bg });
    // Play-field frame.
    g.rect(0, 0, ARENA_W, ARENA_H).stroke({ color: COLOR.dim, width: 2, alpha: 0.5 });
  }

  /** Deterministic little starfield (fixed layout; parallax fall + twinkle). */
  private seedStars(): void {
    let s = 1234567;
    const rnd = () => {
      s = (s * 16807) % 2147483647;
      return s / 2147483647;
    };
    for (let i = 0; i < 70; i++) {
      const depth = rnd(); // 0 = far, 1 = near
      this.starField.push({
        x: rnd() * ARENA_W,
        y: rnd() * ARENA_H,
        size: 1 + depth * 2,
        speed: 8 + depth * 26,
        tw: rnd() * TAU,
      });
    }
  }

  draw(world: World, alpha: number, dt: number): void {
    this.time += dt;
    this.drawStars(dt);
    this.glow.clear();
    this.trails.clear();
    this.drawHazards(world, alpha);
    this.drawEnemies(world, alpha);
    this.drawDragon(world, alpha);
    this.drawHearts(world, alpha);
    this.drawParticles(world, alpha);
    this.drawPlayer(world, alpha);
    this.drawFlash(world);
  }

  private drawStars(dt: number): void {
    const g = this.stars;
    g.clear();
    for (const st of this.starField) {
      st.y += st.speed * dt;
      if (st.y > ARENA_H) {
        st.y -= ARENA_H;
      }
      const a = 0.25 + 0.2 * Math.sin(this.time * 1.7 + st.tw);
      g.circle(st.x, st.y, st.size).fill({ color: COLOR.star, alpha: a });
    }
  }

  private drawHazards(world: World, alpha: number): void {
    const glow = this.glow;
    const tr = this.trails;
    const g = this.hazards;
    g.clear();
    const pool = world.hazards;
    for (let i = 0; i < pool.count; i++) {
      const h = pool.items[i];
      const x = lerp(h.px, h.x, alpha);
      const y = lerp(h.py, h.y, alpha);
      const warning = h.warnT > 0;
      const fade = h.fade;

      if (h.kind === HZ_CIRCLE) {
        if (warning) {
          g.circle(x, y, h.radius).stroke({ color: h.color, width: 2, alpha: 0.6 });
        } else {
          // Motion streak: smear the bullet opposite its velocity.
          const sp = Math.hypot(h.vx, h.vy);
          if (sp > 40) {
            const k = Math.min(0.06, 22 / sp); // streak ~22 units, capped
            tr.moveTo(x - h.vx * k * 3, y - h.vy * k * 3)
              .lineTo(x, y)
              .stroke({ color: h.color, width: h.radius * 1.1, alpha: 0.22 * fade });
          }
          const pulse = 1 + 0.08 * Math.sin(this.time * 10 + x * 0.05);
          glow.circle(x, y, h.radius * pulse + 7).fill({ color: h.color, alpha: 0.16 * fade });
          g.circle(x, y, h.radius * pulse).fill({ color: h.color, alpha: fade });
          g.circle(x, y, h.radius * 0.5).fill({ color: 0xffffff, alpha: 0.3 * fade });
        }
      } else {
        // Rect / beam. Draw in local space using transform.
        const w = h.hw * 2;
        const ht = h.hh * 2;
        if (warning) {
          // Telegraph: pulsing outline + faint fill that charges toward strike.
          const charge = h.warnTotal > 0 ? 1 - h.warnT / h.warnTotal : 0;
          const pulse = 0.4 + 0.3 * Math.sin(this.time * 18);
          drawRect(g, x, y, h.angle, w, ht, undefined, {
            color: h.color,
            width: 3,
            alpha: pulse,
          });
          drawRect(g, x, y, h.angle, w, ht, { color: h.color, alpha: 0.06 + 0.12 * charge });
        } else {
          drawRect(glow, x, y, h.angle, w + 14, ht + 14, { color: h.color, alpha: 0.13 * fade });
          drawRect(g, x, y, h.angle, w, ht, { color: h.color, alpha: 0.95 * fade });
          // Hot inner core line along the long axis.
          drawRect(g, x, y, h.angle, Math.max(4, w - 14), Math.max(3, ht - 14), {
            color: 0xffffff,
            alpha: 0.12 * fade,
          });
        }
      }
    }
  }

  // --- Enemies ---------------------------------------------------------------
  private drawEnemies(world: World, alpha: number): void {
    const g = this.enemies;
    g.clear();
    const pool = world.enemies;
    for (let i = 0; i < pool.count; i++) {
      const e = pool.items[i];
      const x = lerp(e.px, e.x, alpha);
      const y = lerp(e.py, e.y, alpha);

      if (e.dying > 0) {
        // Death: expanding, fading shell.
        const t = 1 - e.dying / 0.45;
        const r = e.radius * (1 + t * 1.6);
        g.circle(x, y, r).stroke({ color: e.color, width: 3, alpha: (1 - t) * 0.9 });
        g.circle(x, y, r * 0.55).fill({ color: 0xffffff, alpha: (1 - t) * 0.35 });
        continue;
      }

      // Spawn warp-in: contracting ring while fading up.
      if (e.fade < 1) {
        const rr = e.radius * (2.4 - 1.4 * e.fade);
        g.circle(x, y, rr).stroke({ color: e.color, width: 2, alpha: 0.6 * e.fade });
      }
      const a = clamp(e.fade * 1.2, 0, 1);
      this.glow.circle(x, y, e.radius + 10).fill({ color: e.color, alpha: 0.14 * a });
      this.drawEnemyBody(g, e, x, y, a);

      // Sword-hit feedback: white flash overlay.
      if (e.hitFlash > 0) {
        g.circle(x, y, e.radius * 1.05).fill({ color: 0xffffff, alpha: 2.5 * e.hitFlash });
      }
      // HP pips once wounded (multi-hit enemies only).
      if (e.maxHp > 1 && e.hp < e.maxHp) {
        const w = 9;
        const x0 = x - ((e.maxHp - 1) * w) / 2;
        const py = y - e.radius - 14;
        for (let p = 0; p < e.maxHp; p++) {
          if (p < e.hp) g.circle(x0 + p * w, py, 3).fill({ color: COLOR.accent, alpha: 0.9 * a });
          else g.circle(x0 + p * w, py, 3).stroke({ color: COLOR.dim, width: 1, alpha: 0.7 * a });
        }
      }
    }
  }

  /** Each archetype gets a distinct silhouette so threats read instantly. */
  private drawEnemyBody(g: Graphics, e: Enemy, x: number, y: number, a: number): void {
    const r = e.radius;
    switch (e.type) {
      case EN_METEOR: {
        // Burning rock: flame streak behind, tumbling craggy body, craters.
        const sp = Math.hypot(e.vx, e.vy) || 1;
        const bx = -e.vx / sp;
        const by = -e.vy / sp;
        const flick = 0.8 + 0.2 * Math.sin(this.time * 26 + e.phase * 7);
        this.trails
          .moveTo(x + bx * r * 3.4 * flick, y + by * r * 3.4 * flick)
          .lineTo(x, y)
          .stroke({ color: COLOR.enemyMeteorFlame, width: r * 1.4, alpha: 0.3 * a });
        this.glow.circle(x + bx * r * 1.2, y + by * r * 1.2, r * 1.3).fill({
          color: COLOR.enemyMeteorFlame,
          alpha: 0.22 * a * flick,
        });
        poly(g, x, y, r, 7, e.phase, { color: e.color, alpha: 0.95 * a });
        // Craters.
        g.circle(x + Math.cos(e.phase) * r * 0.4, y + Math.sin(e.phase) * r * 0.4, r * 0.22)
          .fill({ color: shade(e.color, 0.6), alpha: 0.9 * a });
        g.circle(x - Math.cos(e.phase * 1.3) * r * 0.35, y - Math.sin(e.phase * 1.3) * r * 0.35, r * 0.14)
          .fill({ color: shade(e.color, 0.6), alpha: 0.9 * a });
        // Leading hot edge.
        g.circle(x - bx * r * 0.6, y - by * r * 0.6, r * 0.4).fill({
          color: COLOR.enemyMeteorFlame,
          alpha: 0.5 * a,
        });
        break;
      }
      case EN_ARCHER: {
        // Octopod: dome + wiggling tentacles, eyes on the aim line.
        const wob = Math.sin(this.time * 3) * 2;
        // Tentacles: five stubs under the dome.
        for (let tI = 0; tI < 5; tI++) {
          const tx = x + (tI - 2) * r * 0.42;
          const ty = y + r * 0.55 + Math.sin(this.time * 6 + tI * 1.7) * 4;
          g.circle(tx, ty, r * 0.22).fill({ color: shade(e.color, 0.8), alpha: 0.9 * a });
        }
        // Dome (draw as circle; the tentacle row sells the octopus).
        g.circle(x, y + wob * 0.3, r * 0.95).fill({ color: e.color, alpha: 0.95 * a });
        g.circle(x, y - r * 0.25 + wob * 0.3, r * 0.55).fill({
          color: shade(e.color, 1.25),
          alpha: 0.5 * a,
        });
        // Eyes track the aim; they narrow (recoil) mid-volley.
        const firing = e.phase > 0 && e.mode === MODE_ACT;
        const ex = Math.cos(e.angle) * r * 0.3;
        const ey = Math.sin(e.angle) * r * 0.3;
        for (const side of [-1, 1]) {
          const ox = x + side * r * 0.34 + ex * 0.5;
          const oy = y - r * 0.1 + ey * 0.5 + wob * 0.3;
          g.circle(ox, oy, firing ? r * 0.13 : r * 0.18).fill({ color: 0xffffff, alpha: a });
          g.circle(ox + ex * 0.3, oy + ey * 0.3, r * 0.08).fill({ color: 0x1a1030, alpha: a });
        }
        break;
      }
      case EN_BOMBER: {
        // Waddling sapper: round body, helmet band, stubby legs, bomb in hand.
        const step = Math.sin(this.time * 10) * (e.mode === MODE_ACT ? 0 : 1);
        const facing = Math.cos(e.angle) >= 0 ? 1 : -1;
        // Legs.
        g.circle(x - r * 0.35, y + r * 0.8 + step * 3, r * 0.2).fill({
          color: shade(e.color, 0.7),
          alpha: 0.9 * a,
        });
        g.circle(x + r * 0.35, y + r * 0.8 - step * 3, r * 0.2).fill({
          color: shade(e.color, 0.7),
          alpha: 0.9 * a,
        });
        // Body + helmet.
        g.circle(x, y, r * 0.95).fill({ color: e.color, alpha: 0.95 * a });
        g.moveTo(x - r, y - r * 0.25)
          .lineTo(x + r, y - r * 0.25)
          .stroke({ color: shade(e.color, 0.65), width: 4, alpha: 0.9 * a });
        g.circle(x + facing * r * 0.35, y, r * 0.13).fill({ color: 0x1a1a14, alpha: a });
        // The bomb (held until thrown): black ball + sparking fuse.
        if (e.mode === MODE_ENTER || e.mode === MODE_ACT) {
          const bx = x + facing * r * 0.95;
          const by = y - r * 0.4;
          g.circle(bx, by, r * 0.42).fill({ color: 0x22262e, alpha: a });
          const sp = 0.5 + 0.5 * Math.sin(this.time * 24);
          g.circle(bx + r * 0.18, by - r * 0.5, 3.5).fill({ color: COLOR.accent, alpha: a * sp });
        }
        break;
      }
      case EN_HEN: {
        // The Healing Hen: plump body, comb, beak, wing; squashes on landing.
        const grounded = e.phase === 0;
        const sq = grounded ? 1.15 : 0.92; // squash/stretch
        const facing = Math.cos(e.angle) >= 0 ? 1 : -1;
        // Legs when grounded.
        if (grounded) {
          for (const side of [-0.3, 0.3]) {
            g.moveTo(x + side * r, y + r * 0.7)
              .lineTo(x + side * r, y + r * 1.15)
              .stroke({ color: COLOR.accent, width: 3, alpha: 0.9 * a });
          }
        }
        // Tail feathers.
        for (let f = 0; f < 3; f++) {
          const ta = Math.PI * (facing > 0 ? 1 : 0) + (f - 1) * 0.35 * facing;
          g.circle(x + Math.cos(ta) * r * 0.9, y - r * 0.15 + Math.sin(ta) * r * 0.5, r * 0.28)
            .fill({ color: shade(e.color, 0.85), alpha: 0.9 * a });
        }
        // Body.
        g.ellipse(x, y, r * sq, r / sq).fill({ color: e.color, alpha: 0.97 * a });
        // Wing.
        g.ellipse(x - facing * r * 0.15, y + r * 0.1, r * 0.45, r * 0.3).fill({
          color: shade(e.color, 0.88),
          alpha: 0.9 * a,
        });
        // Head: comb, eye, beak.
        const hx = x + facing * r * 0.55;
        const hy = y - r * 0.55;
        g.circle(hx, hy, r * 0.42).fill({ color: e.color, alpha: 0.97 * a });
        for (let c = 0; c < 3; c++) {
          g.circle(hx - facing * r * 0.18 + facing * c * r * 0.18, hy - r * 0.42, r * 0.13).fill({
            color: COLOR.enemyHenComb,
            alpha: 0.95 * a,
          });
        }
        g.moveTo(hx + facing * r * 0.38, hy - r * 0.06)
          .lineTo(hx + facing * r * 0.72, hy + r * 0.06)
          .lineTo(hx + facing * r * 0.38, hy + r * 0.18)
          .closePath()
          .fill({ color: COLOR.accent, alpha: a });
        g.circle(hx + facing * r * 0.14, hy - r * 0.08, r * 0.08).fill({ color: 0x1a1a14, alpha: a });
        break;
      }
      case EN_BEAST: {
        // Charging beast: massive slab with horns; shakes during telegraph.
        let ox = 0;
        let oy = 0;
        if (e.mode === MODE_ENTER) {
          // Telegraph: rumble + lane warning across the arena.
          const rumble = 3 * Math.sin(this.time * 60);
          ox = e.aimY !== 0 ? rumble : 0;
          oy = e.aimX !== 0 ? rumble : 0;
          const lanePulse = 0.11 + 0.08 * Math.sin(this.time * 14);
          if (e.aimX !== 0) {
            g.rect(0, y - r * 0.9, ARENA_W, r * 1.8).fill({ color: e.color, alpha: lanePulse * a });
          } else {
            g.rect(x - r * 0.9, 0, r * 1.8, ARENA_H).fill({ color: e.color, alpha: lanePulse * a });
          }
        } else if (e.mode === MODE_ACT) {
          // Speed lines while charging.
          this.trails
            .moveTo(x - e.aimX * r * 3.6, y - e.aimY * r * 3.6)
            .lineTo(x, y)
            .stroke({ color: e.color, width: r * 1.5, alpha: 0.18 * a });
        }
        const cx = x + ox;
        const cy = y + oy;
        const dirA = Math.atan2(e.aimY, e.aimX);
        // Body: broad octagon + armored brow.
        poly(g, cx, cy, r, 8, dirA + Math.PI / 8, { color: e.color, alpha: 0.95 * a });
        poly(g, cx, cy, r * 0.62, 8, dirA - Math.PI / 8, { color: shade(e.color, 0.65), alpha: 0.95 * a });
        // Horns: two forward spikes on the charge heading.
        for (const side of [-0.45, 0.45]) {
          const baX = cx + Math.cos(dirA + side) * r * 0.85;
          const baY = cy + Math.sin(dirA + side) * r * 0.85;
          const tipX = cx + Math.cos(dirA + side * 0.55) * r * 1.5;
          const tipY = cy + Math.sin(dirA + side * 0.55) * r * 1.5;
          g.moveTo(baX, baY)
            .lineTo(tipX, tipY)
            .lineTo(cx + Math.cos(dirA + side * 1.4) * r * 0.7, cy + Math.sin(dirA + side * 1.4) * r * 0.7)
            .closePath()
            .fill({ color: COLOR.dim, alpha: a });
        }
        // Furious eyes.
        for (const side of [-0.3, 0.3]) {
          const exx = cx + Math.cos(dirA + side) * r * 0.55;
          const eyy = cy + Math.sin(dirA + side) * r * 0.55;
          g.circle(exx, eyy, r * 0.1).fill({ color: COLOR.accent, alpha: a });
        }
        break;
      }
      case EN_LASER: {
        // Floating eye of energy. Idle: iris tracks. Charging: core swells +
        // warning line down the locked aim. Firing: full recoil flash.
        const charging = e.mode === MODE_ACT && e.phase === 1;
        const firing = e.mode === MODE_ACT && e.phase === 2;
        const chargeT = charging ? clamp(1 - e.t / 1.25, 0, 1) : 0;
        this.glow.circle(x, y, r * (1.6 + chargeT)).fill({
          color: e.color,
          alpha: (0.18 + 0.25 * chargeT) * a,
        });
        if (charging) {
          // Telegraph lance along the locked aim.
          g.moveTo(x, y)
            .lineTo(x + e.aimX * 1760, y + e.aimY * 1760)
            .stroke({ color: e.color, width: 2 + chargeT * 4, alpha: (0.15 + 0.3 * chargeT) * a });
        }
        // Orb + energy ring.
        g.circle(x, y, r).fill({ color: COLOR.enemyLaserCore, alpha: 0.95 * a });
        g.circle(x, y, r).stroke({ color: e.color, width: 3, alpha: 0.9 * a });
        // Iris + pupil.
        const ix = x + Math.cos(e.angle) * r * 0.35;
        const iy = y + Math.sin(e.angle) * r * 0.35;
        g.circle(ix, iy, r * 0.45).fill({ color: e.color, alpha: 0.85 * a });
        const pupil = firing ? r * 0.34 : r * (0.18 + 0.14 * chargeT);
        g.circle(ix, iy, pupil).fill({ color: firing ? 0xffffff : 0x1a1030, alpha: a });
        break;
      }
      case EN_NINJA: {
        const scarf = COLOR.enemyNinjaScarf;
        const dashing = e.mode === MODE_ACT || e.mode === MODE_LEAVE;
        if (dashing) {
          // Airborne: tucked body flipping end-over-end, scarf + streak behind.
          this.trails
            .moveTo(x - e.aimX * r * 3.4, y - e.aimY * r * 3.4)
            .lineTo(x, y)
            .stroke({ color: e.color, width: r * 1.25, alpha: 0.22 * a });
          const total = e.homeX > 0 ? e.homeX : 1;
          const frac = clamp(1 - e.t / total, 0, 1);
          const rot = e.angle + frac * TAU * 2; // two full flips per dash
          poly(g, x, y, r * 0.9, 4, rot, { color: e.color, alpha: 0.96 * a });
          poly(g, x, y, r * 0.55, 4, rot + Math.PI / 4, { color: shade(e.color, 0.7), alpha: 0.9 * a });
          // Scarf whipping behind the flip.
          const sx = x - e.aimX * r * 1.3;
          const sy = y - e.aimY * r * 1.3;
          g.moveTo(x - e.aimX * r * 0.4, y - e.aimY * r * 0.4)
            .lineTo(sx - e.aimY * 6 * Math.sin(this.time * 30), sy + e.aimX * 6 * Math.sin(this.time * 30))
            .stroke({ color: scarf, width: 5, alpha: 0.85 * a });
          // Headband glint marks the "head" through the spin.
          g.circle(x + Math.cos(rot) * r * 0.55, y + Math.sin(rot) * r * 0.55, r * 0.16).fill({
            color: scarf,
            alpha: 0.9 * a,
          });
        } else {
          // Crouched: low coiled stance, eyes on you, wind-up deepens.
          const ct = clamp(e.t / 0.7, 0, 1); // 1 → just landed, 0 → launching
          const coil = 1 + 0.22 * (1 - ct); // flattens as it winds up
          const dirX = Math.cos(e.angle);
          const dirY = Math.sin(e.angle);
          // Aim chevrons: three fading dots toward the player.
          for (let k = 1; k <= 3; k++) {
            const ca = (0.55 - k * 0.12) * (1 - ct) * a;
            g.circle(x + dirX * (r * 1.6 + k * 15), y + dirY * (r * 1.6 + k * 15), 3).fill({
              color: scarf,
              alpha: Math.max(0, ca),
            });
          }
          // Body: squat ellipse + hood.
          g.ellipse(x, y + r * 0.25 * (1 - ct), r * coil, r * 0.8 / coil).fill({
            color: e.color,
            alpha: 0.96 * a,
          });
          g.circle(x + dirX * r * 0.3, y - r * 0.35 + r * 0.25 * (1 - ct), r * 0.55).fill({
            color: shade(e.color, 1.15),
            alpha: 0.96 * a,
          });
          // Eye band: dark stripe with two glinting eyes toward the aim.
          const ex = x + dirX * r * 0.45;
          const ey = y - r * 0.4 + r * 0.25 * (1 - ct);
          g.ellipse(ex, ey, r * 0.4, r * 0.16).fill({ color: 0x141824, alpha: 0.95 * a });
          const glint = 0.7 + 0.3 * Math.sin(this.time * 12);
          for (const side of [-1, 1]) {
            g.circle(ex + side * r * 0.16 - dirY * side * r * 0.02, ey, r * 0.07).fill({
              color: 0xffffff,
              alpha: glint * a,
            });
          }
          // Scarf tail fluttering off the shoulder.
          const fl = Math.sin(this.time * 8) * 0.3;
          g.moveTo(x - dirX * r * 0.5, y - r * 0.3)
            .lineTo(x - dirX * r * 1.5, y - r * 0.5 + fl * r)
            .lineTo(x - dirX * r * 1.2, y - r * 0.05 + fl * r * 0.5)
            .closePath()
            .fill({ color: scarf, alpha: 0.9 * a });
        }
        break;
      }
    }
  }

  // --- Dragon ------------------------------------------------------------------
  private drawDragon(world: World, alpha: number): void {
    const g = this.dragon;
    g.clear();
    const d = world.boss;
    if (!d.active) return;

    const hx = lerp(d.px, d.x, alpha);
    const hy = lerp(d.py, d.y, alpha);

    if (d.dying > 0) {
      // Death sequence: a ripple of expanding, fading shockwave shells runs
      // down the body from head to tail as the dragon comes apart.
      const t = 1 - d.dying / DRAGON.deathTime; // 0 -> 1
      for (let i = 0; i < d.segs.length; i++) {
        const s = d.segs[i];
        const sx = lerp(s.px, s.x, alpha);
        const sy = lerp(s.py, s.y, alpha);
        const localT = clamp(t * 1.6 - i * 0.04, 0, 1);
        if (localT <= 0) continue;
        const r = s.r * (1 + localT * 2.2);
        g.circle(sx, sy, r).stroke({ color: COLOR.dragonFire, width: 3, alpha: (1 - localT) * 0.9 });
        g.circle(sx, sy, r * 0.5).fill({ color: 0xffffff, alpha: (1 - localT) * 0.3 });
      }
      const headT = clamp(t * 1.6, 0, 1);
      const headR = DRAGON.headRadius * (1 + headT * 2.6);
      this.glow.circle(hx, hy, headR * 1.4).fill({ color: COLOR.dragonFire, alpha: (1 - headT) * 0.4 });
      g.circle(hx, hy, headR).stroke({ color: COLOR.dragonFire, width: 4, alpha: (1 - headT) * 0.95 });
      g.circle(hx, hy, headR * 0.55).fill({ color: 0xffffff, alpha: (1 - headT) * 0.4 });
      return;
    }

    // Dive telegraph: a warning lance along the strike direction.
    if (d.telegraph > 0) {
      const len = 1600;
      const ex = hx + Math.cos(d.angle) * len;
      const ey = hy + Math.sin(d.angle) * len;
      g.moveTo(hx, hy)
        .lineTo(ex, ey)
        .stroke({ color: COLOR.hazard, width: 4, alpha: 0.35 * d.telegraph });
    }

    // Body: tail → head so overlaps stack naturally. Undulating fins ride the spine.
    const segs = d.segs;
    for (let i = segs.length - 1; i >= 0; i--) {
      const s = segs[i];
      const sx = lerp(s.px, s.x, alpha);
      const sy = lerp(s.py, s.y, alpha);
      const t = i / (segs.length - 1);
      this.glow.circle(sx, sy, s.r + 9).fill({ color: COLOR.dragonFire, alpha: 0.08 });

      // Dorsal fin every other segment, waving with travel.
      if (i % 2 === 0 && i > 0) {
        const wave = Math.sin(this.time * 6 + i) * 0.3;
        const na = s.angle + Math.PI / 2 + wave;
        const fl = s.r * 1.5;
        g.moveTo(sx, sy)
          .lineTo(sx + Math.cos(na - 0.25) * fl, sy + Math.sin(na - 0.25) * fl)
          .lineTo(sx + Math.cos(na + 0.25) * fl * 0.5, sy + Math.sin(na + 0.25) * fl * 0.5)
          .closePath()
          .fill({ color: COLOR.dragonFin, alpha: 0.55 });
      }

      g.circle(sx, sy, s.r).fill({ color: shade(COLOR.dragonBody, 1 - t * 0.45), alpha: 0.96 });
      g.circle(sx, sy, s.r * 0.55).fill({ color: COLOR.dragonBelly, alpha: 0.35 * (1 - t) });
    }

    // Wings on the shoulder segment: two flapping membranes.
    const sh = segs[1];
    const shx = lerp(sh.px, sh.x, alpha);
    const shy = lerp(sh.py, sh.y, alpha);
    const flap = Math.sin(this.time * 7) * 0.45;
    for (const side of [-1, 1]) {
      const wa = sh.angle + side * (Math.PI / 2 + flap * 0.6);
      const wl = DRAGON.headRadius * 2.6;
      const tipX = shx + Math.cos(wa) * wl;
      const tipY = shy + Math.sin(wa) * wl;
      const midX = shx + Math.cos(wa + side * 0.5) * wl * 0.55;
      const midY = shy + Math.sin(wa + side * 0.5) * wl * 0.55;
      g.moveTo(shx, shy)
        .lineTo(tipX, tipY)
        .lineTo(midX, midY)
        .closePath()
        .fill({ color: shade(COLOR.dragonBody, 0.8), alpha: 0.8 });
      g.moveTo(shx, shy).lineTo(tipX, tipY).stroke({ color: COLOR.dragonFin, width: 3, alpha: 0.6 });
    }

    // Head: skull plate, snout, back-swept horns, glowing eyes, mouth flame.
    const hr = DRAGON.headRadius;
    const ang = d.angle;
    this.glow.circle(hx, hy, hr + 16).fill({ color: COLOR.dragonFire, alpha: 0.14 });
    // Horns.
    for (const side of [-1, 1]) {
      const ha = ang + Math.PI - side * 0.5;
      const hl = hr * 1.6;
      g.moveTo(hx + Math.cos(ang + side * 1.2) * hr * 0.7, hy + Math.sin(ang + side * 1.2) * hr * 0.7)
        .lineTo(hx + Math.cos(ha) * hl, hy + Math.sin(ha) * hl)
        .lineTo(hx + Math.cos(ha + side * 0.35) * hl * 0.55, hy + Math.sin(ha + side * 0.35) * hl * 0.55)
        .closePath()
        .fill({ color: COLOR.dragonFin, alpha: 0.9 });
    }
    g.circle(hx, hy, hr).fill({ color: COLOR.dragonBody, alpha: 1 });
    // Snout: forward wedge.
    const sn = hr * 1.5;
    g.moveTo(hx + Math.cos(ang - 0.5) * hr * 0.8, hy + Math.sin(ang - 0.5) * hr * 0.8)
      .lineTo(hx + Math.cos(ang) * sn, hy + Math.sin(ang) * sn)
      .lineTo(hx + Math.cos(ang + 0.5) * hr * 0.8, hy + Math.sin(ang + 0.5) * hr * 0.8)
      .closePath()
      .fill({ color: shade(COLOR.dragonBody, 0.88), alpha: 1 });
    // Eyes: paired embers that flare during windup.
    const eyeGlow = 0.7 + 0.3 * Math.sin(this.time * 9) + d.telegraph * 0.6;
    for (const side of [-1, 1]) {
      const ea = ang + side * 0.55;
      const ex = hx + Math.cos(ea) * hr * 0.6;
      const ey = hy + Math.sin(ea) * hr * 0.6;
      g.circle(ex, ey, 5).fill({ color: COLOR.dragonEye, alpha: Math.min(1, eyeGlow) });
    }
    // Sword-hit feedback: a white flash across the head and body.
    if (d.hitFlash > 0) {
      g.circle(hx, hy, hr * 1.08).fill({ color: 0xffffff, alpha: 2.5 * d.hitFlash });
      for (const s of segs) {
        const sx = lerp(s.px, s.x, alpha);
        const sy = lerp(s.py, s.y, alpha);
        g.circle(sx, sy, s.r * 1.05).fill({ color: 0xffffff, alpha: 1.6 * d.hitFlash });
      }
    }
    // Mouth flame while firing/breathing.
    if (d.fireGlow > 0) {
      const mx = hx + Math.cos(ang) * sn;
      const my = hy + Math.sin(ang) * sn;
      const fr = 10 + d.fireGlow * 14 + Math.sin(this.time * 40) * 3;
      this.glow.circle(mx, my, fr * 2).fill({ color: COLOR.dragonFire, alpha: 0.3 * d.fireGlow });
      g.circle(mx, my, fr).fill({ color: COLOR.dragonFire, alpha: 0.9 * d.fireGlow });
      g.circle(mx, my, fr * 0.5).fill({ color: 0xffffff, alpha: 0.8 * d.fireGlow });
    }
  }

  private drawParticles(world: World, alpha: number): void {
    const g = this.particles;
    g.clear();
    const pool = world.particles;
    for (let i = 0; i < pool.count; i++) {
      const p = pool.items[i];
      const x = lerp(p.px, p.x, alpha);
      const y = lerp(p.py, p.y, alpha);
      const a = Math.max(0, p.life / p.maxLife);
      // Shrink as they die; brief white-hot flash at birth.
      const s = p.size * (0.4 + 0.6 * a);
      const color = a > 0.85 ? 0xffffff : p.color;
      g.rect(x - s / 2, y - s / 2, s, s).fill({ color, alpha: a });
    }
  }

  private drawHearts(world: World, alpha: number): void {
    const g = this.hearts;
    g.clear();
    const pool = world.hearts;
    for (let i = 0; i < pool.count; i++) {
      const h = pool.items[i];
      const x = lerp(h.px, h.x, alpha);
      const yBase = lerp(h.py, h.y, alpha);
      const y = yBase + Math.sin(this.time * 2 + h.seed) * 5;
      // Fade out just before despawning (if the player never reaches it).
      const fade = h.life > 0.6 ? 1 : Math.max(0, h.life / 0.6);
      const s = h.radius * (1 + 0.1 * Math.sin(this.time * 5 + h.seed));

      this.glow.circle(x, y, s * 1.8).fill({ color: h.color, alpha: 0.2 * fade });
      heartShape(g, x, y, s, { color: h.color, alpha: 0.95 * fade });
      heartShape(g, x - s * 0.15, y - s * 0.2, s * 0.45, { color: 0xffffff, alpha: 0.5 * fade });
    }
  }

  private drawPlayer(world: World, alpha: number): void {
    const g = this.player;
    g.clear();
    const p = world.player;
    if (!p.alive) {
      this.trailCount = 0;
      return;
    }
    const x = lerp(p.px, p.x, alpha);
    const y = lerp(p.py, p.y, alpha);
    const pulse = 0.5 + 0.5 * Math.sin(this.time * 4);

    // Afterimage ribbon: record position each frame, draw fading ghosts.
    this.trailX[this.trailHead] = x;
    this.trailY[this.trailHead] = y;
    this.trailHead = (this.trailHead + 1) % TRAIL_LEN;
    if (this.trailCount < TRAIL_LEN) this.trailCount++;
    const speed = Math.hypot(p.vx, p.vy);
    const trailVis = clamp(speed / 900, 0, 1);
    if (trailVis > 0.02) {
      for (let i = 0; i < this.trailCount; i++) {
        const idx = (this.trailHead - 1 - i + TRAIL_LEN * 2) % TRAIL_LEN;
        const t = i / TRAIL_LEN;
        this.trails
          .circle(this.trailX[idx], this.trailY[idx], PLAYER.radius * (1 - t) * 0.8)
          .fill({ color: COLOR.player, alpha: 0.1 * (1 - t) * trailVis });
      }
    }

    // Sword slash: a blade whipped in a full circle around the avatar, with a
    // swept-sector trail chasing the edge — reads as spinning a sword, not a
    // radial pulse.
    if (world.slashFx > 0) {
      const t = 1 - world.slashFx / SWORD.fxTime; // 0 → 1
      const ease = 1 - (1 - t) * (1 - t) * (1 - t); // fast start, soft finish
      const ang = -Math.PI / 2 + ease * TAU; // one full revolution, starting up
      const fade = 1 - t * t;
      const reach = SWORD.radius;

      // Swept trail: translucent pie-slices fading behind the blade.
      const sweep = 1.5; // radians of visible wake
      const steps = 7;
      for (let k = 0; k < steps; k++) {
        const a0 = ang - ((k + 1) / steps) * sweep;
        const a1 = ang - (k / steps) * sweep;
        g.moveTo(x, y)
          .arc(x, y, reach * 0.97, a0, a1)
          .closePath()
          .fill({ color: COLOR.sword, alpha: 0.2 * fade * (1 - k / steps) });
      }

      // The blade itself: hilt just off the avatar, glowing edge, white core.
      const hiltR = PLAYER.radius * 0.9;
      const hx = x + Math.cos(ang) * hiltR;
      const hy = y + Math.sin(ang) * hiltR;
      const tipX = x + Math.cos(ang) * reach;
      const tipY = y + Math.sin(ang) * reach;
      this.glow.moveTo(hx, hy).lineTo(tipX, tipY).stroke({
        color: COLOR.sword,
        width: 11,
        alpha: 0.4 * fade,
      });
      g.moveTo(hx, hy).lineTo(tipX, tipY).stroke({ color: COLOR.sword, width: 5, alpha: 0.95 * fade });
      g.moveTo(hx, hy).lineTo(tipX, tipY).stroke({ color: 0xffffff, width: 2, alpha: 0.85 * fade });
      // Crossguard: a short perpendicular bar at the hilt.
      const cgX = x + Math.cos(ang) * (hiltR + 7);
      const cgY = y + Math.sin(ang) * (hiltR + 7);
      const pxn = Math.cos(ang + Math.PI / 2) * 9;
      const pyn = Math.sin(ang + Math.PI / 2) * 9;
      g.moveTo(cgX - pxn, cgY - pyn)
        .lineTo(cgX + pxn, cgY + pyn)
        .stroke({ color: COLOR.accent, width: 3.5, alpha: 0.9 * fade });
      // Tip glint.
      g.circle(tipX, tipY, 3.5).fill({ color: 0xffffff, alpha: 0.9 * fade });
    }

    // Blink during i-frames.
    const visible = p.invuln <= 0 || Math.floor(this.time * 20) % 2 === 0;
    if (!visible) return;

    // Velocity stretch: squash the ring slightly along the travel direction.
    const stretch = 1 + clamp(speed / 3000, 0, 0.25);
    const ma = Math.atan2(p.vy, p.vx);

    // Graze ring (faint).
    g.circle(x, y, PLAYER.grazeRadius).stroke({
      color: COLOR.graze,
      width: 1,
      alpha: 0.12 + 0.06 * pulse,
    });
    // Avatar body ring, stretched along motion.
    g.ellipse(x, y, PLAYER.radius * stretch, PLAYER.radius / stretch)
      .stroke({ color: COLOR.player, width: 3, alpha: 0.95 });
    g.circle(x, y, PLAYER.radius - 5).fill({ color: COLOR.player, alpha: 0.18 });
    // Motion tick: a small nose dot on the leading edge when moving.
    if (speed > 120) {
      g.circle(x + Math.cos(ma) * PLAYER.radius, y + Math.sin(ma) * PLAYER.radius, 3).fill({
        color: COLOR.player,
        alpha: 0.8,
      });
    }
    // Tiny hitbox core (the true collision point).
    g.circle(x, y, PLAYER.hitRadius).fill({ color: COLOR.playerCore, alpha: 1 });
  }

  /** Full-arena damage flash, decaying via world.flash. */
  private drawFlash(world: World): void {
    const g = this.flash;
    g.clear();
    if (world.flash <= 0.01) return;
    g.rect(0, 0, ARENA_W, ARENA_H).fill({ color: COLOR.hazard, alpha: 0.22 * world.flash });
  }

  /** Screen-shake offset applied to the arena container by the game layer. */
  shakeOffset(world: World, out: { x: number; y: number }): void {
    const s = world.shake;
    if (s <= 0.01) {
      out.x = 0;
      out.y = 0;
      return;
    }
    out.x = (Math.random() * 2 - 1) * s;
    out.y = (Math.random() * 2 - 1) * s;
  }
}

/** Draw a rotated rectangle centered at (x,y). Fill if `fill` given, else stroke. */
function drawRect(
  g: Graphics,
  x: number,
  y: number,
  angle: number,
  w: number,
  h: number,
  fill?: { color: number; alpha: number },
  stroke?: { color: number; width: number; alpha: number },
): void {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const hw = w / 2;
  const hh = h / 2;
  const corners: Array<[number, number]> = [
    [-hw, -hh],
    [hw, -hh],
    [hw, hh],
    [-hw, hh],
  ];
  g.moveTo(x + corners[0][0] * cos - corners[0][1] * sin, y + corners[0][0] * sin + corners[0][1] * cos);
  for (let i = 1; i < 4; i++) {
    g.lineTo(x + corners[i][0] * cos - corners[i][1] * sin, y + corners[i][0] * sin + corners[i][1] * cos);
  }
  g.closePath();
  if (fill) g.fill(fill);
  if (stroke) g.stroke(stroke);
}

/** Filled regular polygon centered at (x,y). */
function poly(
  g: Graphics,
  x: number,
  y: number,
  r: number,
  sides: number,
  rot: number,
  fill: { color: number; alpha: number },
): void {
  g.moveTo(x + Math.cos(rot) * r, y + Math.sin(rot) * r);
  for (let i = 1; i < sides; i++) {
    const a = rot + (i / sides) * TAU;
    g.lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r);
  }
  g.closePath().fill(fill);
}

/** Classic two-lobe heart centered at (x,y); `s` is the half-width. */
function heartShape(
  g: Graphics,
  x: number,
  y: number,
  s: number,
  fill: { color: number; alpha: number },
): void {
  g.circle(x - s * 0.5, y - s * 0.3, s * 0.55).fill(fill);
  g.circle(x + s * 0.5, y - s * 0.3, s * 0.55).fill(fill);
  g.moveTo(x - s, y - s * 0.12)
    .lineTo(x, y + s)
    .lineTo(x + s, y - s * 0.12)
    .closePath()
    .fill(fill);
}

/** Multiply a 0xRRGGBB color's brightness by `k`. */
function shade(color: number, k: number): number {
  const r = Math.min(255, Math.round(((color >> 16) & 0xff) * k));
  const g = Math.min(255, Math.round(((color >> 8) & 0xff) * k));
  const b = Math.min(255, Math.round((color & 0xff) * k));
  return (r << 16) | (g << 8) | b;
}
